import React, { useState, useCallback } from 'react';
import '../../styles/components/FormPage.css';
import { Textarea as FormTextarea, Select as FormSelect } from '../FormField';
import { apiPost, planningPath } from '../../services/PlanningApiClient';
import { generateId, useFormSaveEvent, firstMissingField } from './_shared';
import { useNotifications } from '../NotificationProvider';
import { VehicleFormFields, VehicleFormData, VEHICLE_STATUS_OPTIONS } from './VehicleFormFields';

interface VehicleFormPageProps {
  onSave: (vehicle: VehicleFormData & { id: string }) => void;
  onCancel: () => void;
}

const blank = (): VehicleFormData => ({
  name: '', type: 'sedan', license_plate: '', color: '',
  capacity: 4, driver_id: '', features: [], status: 'available', notes: '',
});

export const VehicleFormPage: React.FC<VehicleFormPageProps> = ({ onSave, onCancel }) => {
  const { showError } = useNotifications();
  const [form, setForm] = useState<VehicleFormData>(blank());

  const handleSave = useCallback(async () => {
    const missing = firstMissingField([
      { label: 'Name', value: form.name },
      { label: 'License Plate', value: form.license_plate },
    ]);
    if (missing) { showError(missing); return; }
    const vehicle = { ...form, id: generateId() };
    // Awaited, so a rejected save throws to the shared hook (field errors under
    // the fields, plain-language toast) instead of looking like a success.
    await apiPost(planningPath('/logistics/vehicles'), vehicle);
    onSave(vehicle);
  }, [form, onSave, showError]);

  const fieldErrors = useFormSaveEvent(handleSave, { fallback: 'Failed to save the vehicle.' });
  const setFormClearing = (next: VehicleFormData) => {
    (Object.keys(next) as (keyof VehicleFormData)[])
      .filter(k => next[k] !== form[k])
      .forEach(k => fieldErrors.clear(k));
    setForm(next);
  };

  return (
    <div className="detail-layout">
      <div className="inline-form-page">
        <VehicleFormFields form={form} onChange={setFormClearing} errorFor={fieldErrors.errorFor} />
        {/* Notes are an editable field and belong with the other editable
            fields, not in the sidebar. LogisticsManager renders this same
            vehicle form with Notes in the main column already — this page was
            the copy that had it inverted. */}
        <div className="form-group">
          <label>Notes</label>
          <FormTextarea
            placeholder="Any additional information..."
            value={form.notes ?? ''}
            error={fieldErrors.errorFor('notes')}
            onChange={e => { setForm(p => ({ ...p, notes: e.target.value })); fieldErrors.clear('notes'); }}
            rows={4}
          />
        </div>
      </div>
      <div className="detail-sidebar">
        <div className="side-section">
          <h4>Status</h4>
          <FormSelect
            value={form.status}
            error={fieldErrors.errorFor('status')}
            onChange={e => { setForm(p => ({ ...p, status: e.target.value as typeof p.status })); fieldErrors.clear('status'); }}
            options={VEHICLE_STATUS_OPTIONS}
          />
        </div>
      </div>
    </div>
  );
};
